/**
 * Taken from ushooks-ts
 */
import { useEffect, useState } from "react";
/**
 * Gets the script element with the specified source URL.
 * @param {string} src - The source URL of the script to get.
 * @returns {{ node: HTMLScriptElement | null, status: UseScriptStatus | undefined }} The script element and its loading status.
 * @public
 * @example
 * ```tsx
 * const script = getScriptNode(src);
 * ```
 */
function getScriptNode(src) {
    const node = document.querySelector(`script[src="${src}"]`);
    const status = node?.getAttribute("data-status");
    return {
        node,
        status,
    };
}
/**
 * Custom hook that dynamically loads scripts and tracking their loading status.
 * @param {string | null} src - The source URL of the script to load. Set to `null` or omit to prevent loading (optional).
 * @param {UseScriptOptions} [options] - Additional options for controlling script loading (optional).
 * @returns {UseScriptStatus} The status of the script loading, which can be one of 'idle', 'loading', 'ready', or 'error'.
 * @example
 * const scriptStatus = useScript('https://example.com/script.js', { removeOnUnmount: true });
 * // Access the status of the script loading (e.g., 'loading', 'ready', 'error').
 */
export function useScript(src, options) {
    const [status, setStatus] = useState(() => {
        if (!src) {
            return "idle";
        }
        return "loading";
    });
    useEffect(() => {
        if (!src) {
            return;
        }
        const script = getScriptNode(src);
        let scriptNode = script.node;
        if (!scriptNode) {
            scriptNode = document.createElement("script");
            scriptNode.src = src;
            scriptNode.async = true;
            scriptNode.setAttribute("data-status", "loading");
            document.body.appendChild(scriptNode);
            const setAttributeFromEvent = (event) => {
                const scriptStatus = event.type === "load" ? "ready" : "error";
                scriptNode?.setAttribute("data-status", scriptStatus);
            };
            scriptNode.addEventListener("load", setAttributeFromEvent);
            scriptNode.addEventListener("error", setAttributeFromEvent);
        }
        else {
            setStatus(script.status ?? "loading");
        }
        const setStateFromEvent = (event) => {
            const newStatus = event.type === "load" ? "ready" : "error";
            setStatus(newStatus);
        };
        scriptNode.addEventListener("load", setStateFromEvent);
        scriptNode.addEventListener("error", setStateFromEvent);
        return () => {
            if (scriptNode) {
                scriptNode.removeEventListener("load", setStateFromEvent);
                scriptNode.removeEventListener("error", setStateFromEvent);
            }
            if (scriptNode && options?.removeOnUnmount) {
                scriptNode.remove();
            }
        };
    }, [src, options?.removeOnUnmount]);
    return status;
}
